/**
 * Centralized API client that handles authentication headers
 */

import { v4 as uuidv4 } from 'uuid';

import { readErrorEnvelope } from '@/lib/http-error';
import { logger } from '@/lib/logger';

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Enhanced fetch that automatically adds authentication headers
 */
export async function apiClient(url: string, options: ApiRequestOptions = {}): Promise<Response> {
  const { skipAuth: _skipAuth = false, headers = {}, ...restOptions } = options;
  
  // Prepare headers
  const requestHeaders = new Headers(headers);
  
  
  // Authentication headers should be handled by the browser automatically via cookies
  // Authenticated API routes validate the NextAuth session cookie server-side.
  // Client-side code should not directly access authentication tokens
  
  // Make the request
  return fetch(url, {
    ...restOptions,
    headers: requestHeaders,
  });
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (url: string, options?: ApiRequestOptions) => 
    apiClient(url, { ...options, method: 'GET' }),
  
  post: (url: string, body?: unknown, options?: ApiRequestOptions) => 
    apiClient(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  put: (url: string, body?: unknown, options?: ApiRequestOptions) => 
    apiClient(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  delete: (url: string, options?: ApiRequestOptions) => 
    apiClient(url, { ...options, method: 'DELETE' }),
  
  patch: (url: string, body?: unknown, options?: ApiRequestOptions) =>
    apiClient(url, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    }),
};

// ---------------------------------------------------------------------------
// 共通のエラー契約と報告境界（#8046）
//
// - requestJson: 1 回の通信試行。失敗は必ず ApiRequestError（段階・status・所要時間・照合ID付き）で投げる。
//   自身では報告しない（再試行の途中失敗を障害にしないため）。
// - reportApiFailure: 報告境界。想定外の ApiRequestError だけを logger.error へ渡す。
//   同一インスタンスは StructuredLogger 側で 1 回に抑止される。
// - React Query 経由は QueryCache/MutationCache の onError（再試行後の最終失敗で 1 回）から、
//   命令的な操作は runApiOperation から reportApiFailure を呼ぶ。画面は表示と復旧だけを担う。
// ---------------------------------------------------------------------------

export type ApiFailurePhase = 'send' | 'http' | 'read' | 'validate';
export type ApiFailureKind = 'network' | 'http' | 'invalid_response' | 'aborted';

export const CLIENT_REQUEST_ID_HEADER = 'X-Client-Request-Id';

const CLIENT_REQUEST_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;

/**
 * API 側でクライアントの照合IDを受け取る。形式・長さが不正なら捨てる（ログ注入対策）。
 * 試行ごとにクライアントが発行する ID で、Langfuse trace や capability の requestId とは別物。
 */
export function readClientRequestId(headers: Headers): string | undefined {
  const value = headers.get(CLIENT_REQUEST_ID_HEADER);
  return value && CLIENT_REQUEST_ID_PATTERN.test(value) ? value : undefined;
}

interface ApiRequestErrorInit {
  kind: ApiFailureKind;
  phase: ApiFailurePhase;
  operation: string;
  method: string;
  apiPath: string;
  clientRequestId: string;
  durationMs: number;
  status?: number;
  code?: string;
  cause?: unknown;
}

export class ApiRequestError extends Error {
  readonly kind: ApiFailureKind;
  readonly phase: ApiFailurePhase;
  readonly operation: string;
  readonly method: string;
  readonly apiPath: string;
  readonly clientRequestId: string;
  readonly durationMs: number;
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, init: ApiRequestErrorInit) {
    super(message, { cause: init.cause });
    this.name = 'ApiRequestError';
    this.kind = init.kind;
    this.phase = init.phase;
    this.operation = init.operation;
    this.method = init.method;
    this.apiPath = init.apiPath;
    this.clientRequestId = init.clientRequestId;
    this.durationMs = init.durationMs;
    this.status = init.status;
    this.code = init.code;
  }

  /**
   * 障害として報告しない失敗。意図した中断と 4xx（入力不備・認証/権限・利用上限など）は想定内。
   * ponytail: 4xx を一律で想定内とする。業務上の例外を細かく分ける必要が出たら呼び出し側から上書きできるようにする。
   */
  get expected(): boolean {
    if (this.kind === 'aborted') {
      return true;
    }
    return this.kind === 'http' && this.status !== undefined && this.status < 500;
  }

  /** Sentry へ渡してよい診断情報のみ（本文・クエリ文字列は含まない）。 */
  diagnostics(): Record<string, string | number | boolean | undefined> {
    return {
      operation: this.operation,
      apiPath: this.apiPath,
      method: this.method,
      durationMs: this.durationMs,
      failurePhase: this.phase,
      status: this.status,
      errorKind: this.kind,
      errorCode: this.code,
      clientRequestId: this.clientRequestId,
      // 接続状態は補助情報。online=true は通信正常の証拠にならない
      online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
      visibilityState: typeof document === 'undefined' ? undefined : document.visibilityState,
    };
  }
}

const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

/** クエリ文字列と ID を落としたパス。Sentry の集計キーと機密除外を兼ねる。 */
function normalizeApiPath(url: string): string {
  const pathname = url.split(/[?#]/)[0];
  return pathname.replace(UUID_SEGMENT, '/:id').replace(NUMERIC_SEGMENT, '/:id');
}

/** `/` 始まりの同一オリジンパス。`//host` や `/\host` はブラウザが外部オリジンとして解決するため除く。 */
const SAME_ORIGIN_PATH = /^\/(?![/\\])/;

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export interface RequestJsonOptions<T> {
  /** 集計・検索用の操作名（例: 'transcription.format-minutes'） */
  operation: string;
  /** 同一オリジンの API パス（`/api/...`）。外部 URL には照合ヘッダーを付けない。 */
  url: string;
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** 本文を読めない / 解釈できないときに画面へ出す文言 */
  fallbackMessage: string;
  /** 成功応答の検証。throw した Error の message が画面表示用文言になる。 */
  parse: (payload: unknown) => T;
}

type FailureDetail = Pick<ApiRequestErrorInit, 'kind' | 'phase' | 'status' | 'code' | 'cause'>;

async function sendRequest(
  options: RequestJsonOptions<unknown>,
  clientRequestId: string,
  fail: (message: string, detail: FailureDetail) => ApiRequestError,
): Promise<Response> {
  const { url, method = 'GET', body, signal, fallbackMessage } = options;
  const headers = new Headers();
  if (SAME_ORIGIN_PATH.test(url)) {
    headers.set(CLIENT_REQUEST_ID_HEADER, clientRequestId);
  }
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  // 直列化の失敗は呼び出し側のバグであり通信失敗ではないため、try の外で行う
  const serializedBody = body === undefined ? undefined : JSON.stringify(body);
  try {
    return await fetch(url, { method, headers, body: serializedBody, signal });
  } catch (error) {
    // 画面表示は従来どおり元の例外メッセージ（例: "Failed to fetch"）
    throw fail(error instanceof Error ? error.message : fallbackMessage, {
      kind: isAbortError(error) ? 'aborted' : 'network',
      phase: 'send',
      cause: error,
    });
  }
}

async function readPayload(
  response: Response,
  fallbackMessage: string,
  fail: (message: string, detail: FailureDetail) => ApiRequestError,
): Promise<{ payload: unknown; parseError?: unknown }> {
  try {
    return { payload: await response.json() };
  } catch (error) {
    // 本文が JSON でない（空本文を含む）場合は SyntaxError。それ以外は本文受信中の切断・中断。
    if (error instanceof SyntaxError) {
      return { payload: null, parseError: error };
    }
    throw fail(fallbackMessage, {
      kind: isAbortError(error) ? 'aborted' : 'network',
      phase: 'read',
      status: response.status,
      cause: error,
    });
  }
}

/** 1 回の通信試行。失敗は常に ApiRequestError として投げる。 */
export async function requestJson<T>(options: RequestJsonOptions<T>): Promise<T> {
  const { operation, url, method = 'GET', fallbackMessage, parse } = options;
  // crypto.randomUUID は secure context 限定のため uuid を使う
  const clientRequestId = uuidv4();
  const startedAt = Date.now();
  const fail = (message: string, detail: FailureDetail) =>
    new ApiRequestError(message, {
      ...detail,
      operation,
      method,
      apiPath: normalizeApiPath(url),
      clientRequestId,
      durationMs: Date.now() - startedAt,
    });

  const response = await sendRequest(options, clientRequestId, fail);
  const { payload, parseError } = await readPayload(response, fallbackMessage, fail);

  if (!response.ok) {
    const envelope = readErrorEnvelope(payload);
    throw fail(envelope.message ?? fallbackMessage, {
      kind: 'http',
      phase: 'http',
      status: response.status,
      code: envelope.code,
    });
  }
  if (parseError) {
    throw fail(fallbackMessage, {
      kind: 'invalid_response',
      phase: 'validate',
      status: response.status,
      cause: parseError,
    });
  }
  try {
    return parse(payload);
  } catch (error) {
    throw fail(error instanceof Error ? error.message : fallbackMessage, {
      kind: 'invalid_response',
      phase: 'validate',
      status: response.status,
      code: readErrorEnvelope(payload).code,
      cause: error,
    });
  }
}

/** 報告境界。想定外の失敗だけを報告する。報告処理の失敗は呼び出し元へ波及させない。 */
export function reportApiFailure(error: unknown): void {
  if (!(error instanceof ApiRequestError) || error.expected) {
    return;
  }
  try {
    logger.error(`[api] ${error.operation} failed`, { error, ...error.diagnostics() });
  } catch {
    // 報告の失敗で操作結果や表示を壊さない
  }
}

export type ApiOutcome<T> =
  | { status: 'ok'; data: T }
  | { status: 'error'; error: ApiRequestError; message: string }
  | { status: 'aborted' };

/**
 * React Query を使わない命令的な操作の境界。結果を判別可能な形に正規化し、
 * 想定外の最終失敗を報告する。ApiRequestError 以外（呼び出し側のバグ）はそのまま再送出する。
 */
export async function runApiOperation<T>(run: () => Promise<T>): Promise<ApiOutcome<T>> {
  try {
    return { status: 'ok', data: await run() };
  } catch (error) {
    if (!(error instanceof ApiRequestError)) {
      throw error;
    }
    if (error.kind === 'aborted') {
      return { status: 'aborted' };
    }
    reportApiFailure(error);
    return { status: 'error', error, message: error.message };
  }
}